import { InjectionToken, Provider } from '@angular/core';

/**
 * Authoritative mapping of legacy/deprecated IANA timezone aliases to canonical IANA timezone identifiers. Sourced from
 * the official IANA Time Zone Database (tzdb) `backward` link registry.
 *
 * Browsers running on V8/WebKit/Gecko follow Unicode CLDR (BCP 47), which historically preferred aliases such as
 * 'Asia/Calcutta' as canonical identifiers. Modern Linux backend servers and containers strip legacy tzdata links,
 * requiring canonical IANA zone names (e.g., 'Asia/Kolkata').
 */
export const IANA_LEGACY_TIMEZONE_MAP: ReadonlyMap<string, string> = new Map([
  // India
  ['Asia/Calcutta', 'Asia/Kolkata'],

  // United States & North America (POSIX legacy aliases)
  ['US/Eastern', 'America/New_York'],
  ['US/Central', 'America/Chicago'],
  ['US/Mountain', 'America/Denver'],
  ['US/Pacific', 'America/Los_Angeles'],
  ['US/Alaska', 'America/Anchorage'],
  ['US/Arizona', 'America/Phoenix'],
  ['US/Hawaii', 'Pacific/Honolulu'],
  ['US/Aleutian', 'America/Adak'],
  ['US/East-Indiana', 'America/Indiana/Indianapolis'],
  ['US/Indiana-Starke', 'America/Indiana/Knox'],
  ['US/Michigan', 'America/Detroit'],
  ['US/Samoa', 'Pacific/Pago_Pago'],
  ['Navajo', 'America/Denver'],

  // Canada
  ['Canada/Eastern', 'America/Toronto'],
  ['Canada/Central', 'America/Winnipeg'],
  ['Canada/Mountain', 'America/Edmonton'],
  ['Canada/Pacific', 'America/Vancouver'],
  ['Canada/Atlantic', 'America/Halifax'],
  ['Canada/Newfoundland', 'America/St_Johns'],
  ['Canada/Yukon', 'America/Whitehorse'],

  // Geopolitical & IANA historical zone renames
  ['Europe/Kiev', 'Europe/Kyiv'],
  ['Asia/Saigon', 'Asia/Ho_Chi_Minh'],
  ['Asia/Katmandu', 'Asia/Kathmandu'],
  ['Asia/Rangoon', 'Asia/Yangon'],
  ['Asia/Ulan_Bator', 'Asia/Ulaanbaatar'],
  ['Asia/Thimbu', 'Asia/Thimphu'],
  ['Asia/Dacca', 'Asia/Dhaka'],
  ['Asia/Ashkhabad', 'Asia/Ashgabat'],
  ['Asia/Chungking', 'Asia/Chongqing'],
  ['Asia/Macao', 'Asia/Macau'],
  ['Asia/Tel_Aviv', 'Asia/Jerusalem'],
  ['Asia/Ujung_Pandang', 'Asia/Makassar'],
  ['America/Godthab', 'America/Nuuk'],
  ['America/Buenos_Aires', 'America/Argentina/Buenos_Aires'],
  ['America/Catamarca', 'America/Argentina/Catamarca'],
  ['America/Cordoba', 'America/Argentina/Cordoba'],
  ['America/Jujuy', 'America/Argentina/Jujuy'],
  ['America/Mendoza', 'America/Argentina/Mendoza'],
  ['America/Rosario', 'America/Argentina/Cordoba'],
  ['America/Indianapolis', 'America/Indiana/Indianapolis'],
  ['America/Knox_IN', 'America/Indiana/Knox'],
  ['America/Shiprock', 'America/Denver'],
  ['America/Atka', 'America/Adak'],
  ['America/Ensenada', 'America/Tijuana'],
  ['America/Porto_Acre', 'America/Rio_Branco'],
  ['America/Virgin', 'America/St_Thomas'],
  ['Atlantic/Faeroe', 'Atlantic/Faroe'],
  ['Africa/Asmera', 'Africa/Asmara'],
  ['Africa/Timbuktu', 'Africa/Abidjan'],

  // Australia
  ['Australia/ACT', 'Australia/Sydney'],
  ['Australia/Canberra', 'Australia/Sydney'],
  ['Australia/NSW', 'Australia/Sydney'],
  ['Australia/LHI', 'Australia/Lord_Howe'],
  ['Australia/North', 'Australia/Darwin'],
  ['Australia/Queensland', 'Australia/Brisbane'],
  ['Australia/South', 'Australia/Adelaide'],
  ['Australia/Tasmania', 'Australia/Hobart'],
  ['Australia/Victoria', 'Australia/Melbourne'],
  ['Australia/West', 'Australia/Perth'],
  ['Australia/Yancowinna', 'Australia/Broken_Hill'],

  // South America & Mexico
  ['Brazil/Acre', 'America/Rio_Branco'],
  ['Brazil/DeNoronha', 'America/Noronha'],
  ['Brazil/East', 'America/Sao_Paulo'],
  ['Brazil/West', 'America/Manaus'],
  ['Chile/Continental', 'America/Santiago'],
  ['Chile/EasterIsland', 'Pacific/Easter'],
  ['Cuba', 'America/Havana'],
  ['Jamaica', 'America/Jamaica'],
  ['Mexico/BajaNorte', 'America/Tijuana'],
  ['Mexico/BajaSur', 'America/Mazatlan'],
  ['Mexico/General', 'America/Mexico_City'],

  // Europe, Asia & Pacific Country Aliases
  ['Egypt', 'Africa/Cairo'],
  ['Eire', 'Europe/Dublin'],
  ['GB', 'Europe/London'],
  ['GB-Eire', 'Europe/London'],
  ['Hongkong', 'Asia/Hong_Kong'],
  ['Iceland', 'Atlantic/Reykjavik'],
  ['Iran', 'Asia/Tehran'],
  ['Israel', 'Asia/Jerusalem'],
  ['Japan', 'Asia/Tokyo'],
  ['Kwajalein', 'Pacific/Kwajalein'],
  ['Libya', 'Africa/Tripoli'],
  ['NZ', 'Pacific/Auckland'],
  ['NZ-CHAT', 'Pacific/Chatham'],
  ['PRC', 'Asia/Shanghai'],
  ['Poland', 'Europe/Warsaw'],
  ['Portugal', 'Europe/Lisbon'],
  ['ROC', 'Asia/Taipei'],
  ['ROK', 'Asia/Seoul'],
  ['Singapore', 'Asia/Singapore'],
  ['Turkey', 'Europe/Istanbul'],
  ['W-SU', 'Europe/Moscow'],

  // UTC/GMT Standard Aliases
  ['Zulu', 'UTC'],
  ['Universal', 'UTC'],
  ['Greenwich', 'UTC'],
  ['GMT', 'UTC'],
]);

// Module-level memoization cache for ultra-fast O(1) repeated access
let cachedCanonicalUserTimeZone: string | null = null;

/**
 * Normalizes a given timezone string to its canonical IANA timezone name.
 *
 * @param timeZone - The timezone string to normalize (e.g., 'Asia/Calcutta', 'US/Eastern').
 * @param customOverrides - Optional custom mapping of aliases to canonical names.
 * @returns Canonical IANA timezone identifier (e.g., 'Asia/Kolkata', 'America/New_York').
 */
export function canonicalizeTimeZone(
  timeZone: string | null | undefined,
  customOverrides?: Record<string, string> | ReadonlyMap<string, string>
): string {
  if (!timeZone || typeof timeZone !== 'string') {
    return 'UTC';
  }

  const trimmed = timeZone.trim();
  if (!trimmed) {
    return 'UTC';
  }

  // 1. Check custom runtime overrides if provided
  if (customOverrides) {
    if (customOverrides instanceof Map && customOverrides.has(trimmed)) {
      return customOverrides.get(trimmed)!;
    }
    if (!(customOverrides instanceof Map) && Object.prototype.hasOwnProperty.call(customOverrides, trimmed)) {
      return (customOverrides as Record<string, string>)[trimmed]!;
    }
  }

  // 2. Check authoritative IANA tzdb backward link map
  const canonical = IANA_LEGACY_TIMEZONE_MAP.get(trimmed);
  if (canonical) {
    return canonical;
  }

  // 3. Fallback to raw timezone (already canonical or standard IANA zone)
  return trimmed;
}

/**
 * Resolves the raw client timezone from the browser's Intl runtime. Defaults to 'UTC' in non-browser or unsupported
 * environments.
 */
export function detectBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Resolves and normalizes the browser timezone to a canonical IANA timezone. Results are memoized across the
 * application lifecycle for zero overhead on repeated HTTP requests.
 *
 * @param customOverrides - Optional custom mapping of aliases to canonical names.
 */
export function getCanonicalUserTimezone(
  customOverrides?: Record<string, string> | ReadonlyMap<string, string>
): string {
  if (!customOverrides && cachedCanonicalUserTimeZone) {
    return cachedCanonicalUserTimeZone;
  }

  const detected = detectBrowserTimeZone();
  const canonical = canonicalizeTimeZone(detected, customOverrides);

  if (!customOverrides) {
    cachedCanonicalUserTimeZone = canonical;
  }

  return canonical;
}

/** Resets the memoized canonical timezone. Intended primarily for unit testing. */
export function resetTimezoneCache(): void {
  cachedCanonicalUserTimeZone = null;
}

/** Canonicalizer function contract for Angular Dependency Injection. */
export type TimezoneCanonicalizerFn = (timeZone: string) => string;

/** Angular InjectionToken for the dynamic timezone canonicalizer. Defaults to `canonicalizeTimeZone`. */
export const TIMEZONE_CANONICALIZER = new InjectionToken<TimezoneCanonicalizerFn>('TIMEZONE_CANONICALIZER', {
  providedIn: 'root',
  factory: () => (tz: string) => canonicalizeTimeZone(tz),
});

/** Optional Angular InjectionToken for custom timezone alias overrides. */
export const TIMEZONE_CUSTOM_ALIASES = new InjectionToken<Record<string, string>>('TIMEZONE_CUSTOM_ALIASES', {
  providedIn: 'root',
  factory: () => ({}),
});

/**
 * Standalone Angular provider function to configure dynamic timezone canonicalization.
 *
 * @example
 *   ```typescript
 *   // In app.config.ts:
 *   export const appConfig: ApplicationConfig = {
 *     providers: [
 *       provideTimezoneCanonicalizer({ 'Custom/Zone': 'America/New_York' }),
 *     ]
 *   };
 *   ```;
 */
export function provideTimezoneCanonicalizer(customAliases?: Record<string, string>): Provider[] {
  return [
    {
      provide: TIMEZONE_CUSTOM_ALIASES,
      useValue: customAliases ?? {},
    },
    {
      provide: TIMEZONE_CANONICALIZER,
      useFactory: () => {
        return (tz: string) => canonicalizeTimeZone(tz, customAliases);
      },
    },
  ];
}
